import type { ICatalogPagination, IOffer, TCatalogError, TCatalogSort } from "~/types/catalog";
import { Filter } from "../filter";
import type { IBaseCatalogPayload } from "~/types/payload";
import { useApi } from "..";
import type { IApiSearchCatalogDataResponse } from "~/types/api";
import type { IFilterField } from "~/types/filter";
import { cloneDeep, isEqual } from "lodash-es";

export class BaseCatalog {

    public readonly loading = ref(false);
    public readonly pagination = reactive<ICatalogPagination>({
        current: 1,
        start: 1,
        total: 1
    });
    public readonly sort = ref<TCatalogSort>('default');
    public readonly error = ref<TCatalogError>(null);
    public readonly pages = shallowReactive<IOffer[][]>([]);
    public readonly filter = shallowRef<Filter | null>(null);
    protected savedRequest: any = null;
    protected oldFilter: any = null;

    public async load() {
        this.loading.value = true;

        try {
            this.error.value = null;

            const data = await useApi().request<IApiSearchCatalogDataResponse>('/rest/user/list/catalog', {
                page: this.pagination.current,
                order: this.sort.value,
                filter: [],
                withFilter: true,
                params: null,
                type: 'favorites'
            });

            if (!data) {
                this.error.value = 'unknown';
                return;
            }

            this.pages.push(data.offers);

            if (data.filter) {
                this.useFilter(data.filter);
            }
        } catch (err) {
            this.handleError(err);
        } finally {
            this.loading.value = false;
        }
    }

    public applyRequestQuery(request: any) {
        if (!request || typeof request !== 'object') {
            return;
        }

        this.savedRequest = request;

        if (typeof request.page === 'string') {
            const page = Number(request.page);
            if (!isNaN(page) && page > 0) {
                this.pagination.current = page;
                this.pagination.start = page;
                this.pagination.total = page;
            }
        }

        if (typeof request.sort === 'string') {
            const allowedTypes = new Set<TCatalogSort>(['cheap', 'expensive', 'new']);
            if (allowedTypes.has(request.sort)) {
                this.sort.value = request.sort;
            }
        }
    }

    protected useFilter(fields: IFilterField[]) {
        if (!this.filter.value) {
            this.filter.value = new Filter(fields);
        } else {
            this.filter.value.patch(fields);
        }

        if (this.filter.value) {
            this.oldFilter = cloneDeep(this.filter.value.activeValues.value);
        }
    }

    protected isShouldLoadFilter() {
        return !this.filter.value || !isEqual(this.oldFilter, this.filter.value.activeValues.value);
    }

    protected getFilterRequest() {
        if (!this.filter.value) {
            return [];
        }

        return this.filter.value.activeValues.value;
    }

    protected handleError(err: any) {
        if (isNuxtError(err)) {
            switch (err.status) {
                case 404:
                case 402:
                    this.error.value = 'not-found'; break;
                case 403:
                    this.error.value = 'unavailable'; break;
                default:
                    this.error.value = 'unknown'; break;
            }
        } else {
            this.error.value = 'unknown';
        }
    }

    protected __restore(data: IBaseCatalogPayload): void {
        this.sort.value = data.sort;

        // т.к. у нас свойство реактивое, его нельзя просто перезаписывать через =
        this.pages.length = 0;
        this.pages.push(...data.pages);

        this.pagination.current = data.pagination.current;
        this.pagination.start = data.pagination.start;
        this.pagination.total = data.pagination.total;

        this.error.value = data.error;
    }

    protected __save(): IBaseCatalogPayload {
        return {
            pages: toValue(this.pages),
            sort: toValue(this.sort),
            pagination: toValue(this.pagination),
            filter: toValue(this.filter),
            error: toValue(this.error),
        };
    }

    public static registerPayload() {
        definePayloadReducer(this.name, (data: BaseCatalog) => data instanceof BaseCatalog && data.__save());
        definePayloadReviver(this.name, (data: any) => {
            const ctor = Object.getPrototypeOf(this);
            const obj: BaseCatalog = new ctor();
            obj.__restore(data);
            return obj;
        });
    }
}
